import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Calendar, Clock, Mic, ClipboardList } from 'lucide-react';

const SettingsPanel = ({ 
  game, 
  gameId,
  lineupStatus,
  onUpdateCompetition, 
  onUpdateTeamName,
  onUpdateMatchInfo
}) => {
  const copyToClipboard = (text, successMessage = 'Copied!') => {
    // Use textarea fallback for better compatibility
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(successMessage);
    } catch (err) {
      window.prompt('Copy this URL:', text);
    }
  };

  return (
    <div className="space-y-4">
      {/* Match Info Card */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-body">Match Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-400 font-body">Competition Name</Label>
            <Input
              data-testid="settings-competition-name"
              value={game.competition_name || ''}
              onChange={(e) => onUpdateCompetition(e.target.value)}
              className="bg-[#0F172A] border-slate-600 text-white font-body"
              placeholder="e.g., U.S. OPEN CUP ROUND 2"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-400 font-body flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Venue
            </Label>
            <Input
              data-testid="settings-venue"
              value={game.venue || ''}
              onChange={(e) => onUpdateMatchInfo('venue', e.target.value)}
              className="bg-[#0F172A] border-slate-600 text-white font-body"
              placeholder="e.g., Wembley Stadium"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-400 font-body flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Date
              </Label>
              <Input
                data-testid="settings-match-date"
                value={game.match_date || ''}
                onChange={(e) => onUpdateMatchInfo('match_date', e.target.value)}
                className="bg-[#0F172A] border-slate-600 text-white font-body"
                placeholder="e.g., Jan 15, 2025"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 font-body flex items-center gap-2">
                <Clock className="w-4 h-4" /> Time
              </Label>
              <Input
                data-testid="settings-match-time"
                value={game.match_time || ''}
                onChange={(e) => onUpdateMatchInfo('match_time', e.target.value)}
                className="bg-[#0F172A] border-slate-600 text-white font-body"
                placeholder="e.g., 7:30 PM"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-body">Team Names</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-400 font-body">Team 1 Name</Label>
            <Input
              data-testid="settings-team1-name"
              value={game.team1.name}
              onChange={(e) => onUpdateTeamName(1, e.target.value)}
              className="bg-[#0F172A] border-slate-600 text-white font-body"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-400 font-body">Team 2 Name</Label>
            <Input
              data-testid="settings-team2-name"
              value={game.team2.name}
              onChange={(e) => onUpdateTeamName(2, e.target.value)}
              className="bg-[#0F172A] border-slate-600 text-white font-body"
            />
          </div>
        </CardContent>
      </Card>

      {/* All Overlay URLs */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-body">Overlay URLs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 font-body text-sm">
            Use these URLs in OBS/Wirecast as Browser Sources:
          </p>
          
          {/* Main Scoreboard */}
          <div className="space-y-2">
            <Label className="text-slate-300 font-body text-xs">Main Scoreboard</Label>
            <div className="flex gap-2">
              <div className="flex-1 bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
                /api/overlay-html/{gameId}
              </div>
              <Button
                size="sm"
                data-testid="copy-overlay-url"
                onClick={() => copyToClipboard(
                  `${process.env.REACT_APP_BACKEND_URL}/api/overlay-html/${gameId}`,
                  'Copied!'
                )}
                className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
              >
                Copy
              </Button>
            </div>
          </div>
          
          {/* Team Intro */}
          <div className="space-y-2">
            <Label className="text-slate-300 font-body text-xs">Team Intro Graphic</Label>
            <div className="flex gap-2">
              <div className="flex-1 bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
                /api/overlay-intro/{gameId}
              </div>
              <Button
                size="sm"
                data-testid="copy-intro-overlay-url"
                onClick={() => copyToClipboard(
                  `${process.env.REACT_APP_BACKEND_URL}/api/overlay-intro/${gameId}`,
                  'Copied!'
                )}
                className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
              >
                Copy
              </Button>
            </div>
          </div>
          
          {/* Bottom Score Bug */}
          <div className="space-y-2">
            <Label className="text-slate-300 font-body text-xs">Bottom Score Bug (with scorers)</Label>
            <div className="flex gap-2">
              <div className="flex-1 bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
                /api/overlay-score-bottom/{gameId}
              </div>
              <Button
                size="sm"
                data-testid="copy-bottom-score-url"
                onClick={() => copyToClipboard(
                  `${process.env.REACT_APP_BACKEND_URL}/api/overlay-score-bottom/${gameId}`,
                  'Copied!'
                )}
                className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
              >
                Copy
              </Button>
            </div>
          </div>
          
          {/* Lineup Overlays */}
          <div className="space-y-2">
            <Label className="text-slate-300 font-body text-xs">Lineup - Team 1</Label>
            <div className="flex gap-2">
              <div className="flex-1 bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
                /api/overlay-lineup/{gameId}?team=1
              </div>
              <Button
                size="sm"
                onClick={() => copyToClipboard(
                  `${process.env.REACT_APP_BACKEND_URL}/api/overlay-lineup/${gameId}?team=1`,
                  'Copied!'
                )}
                className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
              >
                Copy
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-300 font-body text-xs">Lineup - Team 2</Label>
            <div className="flex gap-2">
              <div className="flex-1 bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
                /api/overlay-lineup/{gameId}?team=2
              </div>
              <Button
                size="sm"
                onClick={() => copyToClipboard(
                  `${process.env.REACT_APP_BACKEND_URL}/api/overlay-lineup/${gameId}?team=2`,
                  'Copied!'
                )}
                className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
              >
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Text Feed */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-body">Text Feed URL</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 font-body text-sm mb-2">
            Embed this URL on another website to show live score & time:
          </p>
          <div className="bg-[#0F172A] p-3 rounded font-mono text-sm text-[#D4E5ED] break-all">
            {process.env.REACT_APP_BACKEND_URL}/api/feed/text/{gameId}
          </div>
          <p className="text-slate-500 font-body text-xs mt-2">
            Output: "2-1 (57')" or "2-1 (HT)" • Add ?format=full for team names
          </p>
          <Button
            data-testid="copy-text-feed-url"
            onClick={() => copyToClipboard(
              `${process.env.REACT_APP_BACKEND_URL}/api/feed/text/${gameId}`,
              'Copied!'
            )}
            className="mt-2 w-full bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body touch-target"
          >
            Copy URL
          </Button>
        </CardContent>
      </Card>

      {/* Wirecast / OBS scoreboard text-file feed */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white font-body">Wirecast Scoreboard (Text File)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-slate-400 font-body text-sm">
            Wirecast can read a local <code className="text-[#D4E5ED]">.txt</code> file as a text source.
            Run the PowerShell script below on your broadcast PC — it polls this URL once per second
            and overwrites the local file so Wirecast updates automatically.
          </p>
          <div className="bg-[#0F172A] p-3 rounded font-mono text-sm text-[#D4E5ED] break-all">
            {process.env.REACT_APP_BACKEND_URL}/api/scoreboard-txt/{gameId}
          </div>
          <p className="text-slate-500 font-body text-xs">
            Output: <code>"3 - 2"</code> for soccer/rugby • <code>"2 - 14 | 1 - 9"</code> for Gaelic Games
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="copy-scoreboard-txt-url"
              onClick={() => copyToClipboard(
                `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard-txt/${gameId}`,
                'Scoreboard URL copied!'
              )}
              className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body touch-target"
            >
              Copy URL
            </Button>
            <Button
              data-testid="download-scoreboard-ps1"
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard-txt/${gameId}`;
                const script = [
                  '# update-scoreboard.ps1 - keeps SCOREBOARD.TXT in sync with the live game',
                  '# Generated by Scoreboard control panel. Leave running during the broadcast.',
                  `$Url  = "${url}"`,
                  '$Dest = "C:\\Users\\Patrick\\Google Drive\\STREAMING\\SCOREBOARD.TXT"',
                  '',
                  'Write-Host "Updating $Dest from $Url every 1s. Ctrl+C to stop."',
                  'while ($true) {',
                  '    try {',
                  '        $text = (Invoke-WebRequest -Uri $Url -UseBasicParsing -TimeoutSec 5).Content',
                  '        Set-Content -Path $Dest -Value $text -Encoding UTF8 -NoNewline',
                  '    } catch {',
                  '        # Network blip - retry next tick',
                  '    }',
                  '    Start-Sleep -Seconds 1',
                  '}',
                  '',
                ].join('\r\n');
                const blob = new Blob([script], { type: 'text/plain;charset=utf-8' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'update-scoreboard.ps1';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
                toast.success('Script downloaded. Right-click → Run with PowerShell.');
              }}
              className="bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body touch-target"
            >
              Download .ps1
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lineup Status */}
      {lineupStatus && (lineupStatus.team1.has_database_team || lineupStatus.team2.has_database_team) && (
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-body">Lineup Submission</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {lineupStatus.team1.has_database_team && (
              <div className="bg-[#0F172A] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-body">{lineupStatus.team1.name}</span>
                  <span className={`text-sm font-body px-2 py-1 rounded ${lineupStatus.team1.lineup_submitted ? 'bg-green-600/30 text-green-400' : 'bg-yellow-600/30 text-yellow-400'}`}>
                    {lineupStatus.team1.lineup_submitted ? '✓ Submitted' : '⏳ Pending'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid="copy-lineup-link-team1"
                    onClick={() => {
                      const token = lineupStatus.team1.lineup_url;
                      if (!token) {
                        toast.error('No lineup link available');
                        return;
                      }
                      copyToClipboard(
                        `${window.location.origin}/lineup?token=${token}`,
                        'Link copied!'
                      );
                    }}
                    className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-body text-xs"
                  >
                    Copy Link for Manager
                  </Button>
                </div>
              </div>
            )}
            
            {lineupStatus.team2.has_database_team && (
              <div className="bg-[#0F172A] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-body">{lineupStatus.team2.name}</span>
                  <span className={`text-sm font-body px-2 py-1 rounded ${lineupStatus.team2.lineup_submitted ? 'bg-green-600/30 text-green-400' : 'bg-yellow-600/30 text-yellow-400'}`}>
                    {lineupStatus.team2.lineup_submitted ? '✓ Submitted' : '⏳ Pending'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid="copy-lineup-link-team2"
                    onClick={() => {
                      const token = lineupStatus.team2.lineup_url;
                      if (!token) {
                        toast.error('No lineup link available');
                        return;
                      }
                      copyToClipboard(
                        `${window.location.origin}/lineup?token=${token}`,
                        'Link copied!'
                      );
                    }}
                    className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-body text-xs"
                  >
                    Copy Link for Manager
                  </Button>
                </div>
              </div>
            )}
            
            <p className="text-slate-500 font-body text-xs text-center">
              Share these links with team managers to submit lineups
            </p>
          </CardContent>
        </Card>
      )}

      {/* Commentator Link */}
      {lineupStatus?.commentator_token && (
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-body flex items-center gap-2">
              <Mic className="w-5 h-5 text-purple-400" />
              Commentator Page
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-400 font-body text-sm">
              Share this read-only page with commentators to view both lineups and player/team facts.
            </p>
            <Button
              data-testid="copy-commentator-link"
              onClick={() => copyToClipboard(
                `${window.location.origin}/commentator?token=${lineupStatus.commentator_token}`,
                'Commentator link copied!'
              )}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body"
            >
              Copy Commentator Link
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 4th Official Link */}
      {lineupStatus?.fourth_official_token && (
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-body flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-400" />
              4th Official Panel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-400 font-body text-sm">
              Share this private link with the 4th official to log match events and download reports.
            </p>
            <Button
              data-testid="copy-fourth-official-link"
              onClick={() => copyToClipboard(
                `${window.location.origin}/fourth-official?token=${lineupStatus.fourth_official_token}`,
                '4th Official link copied!'
              )}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-body"
            >
              Copy 4th Official Link
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPanel;
